/**
 * Income-statement view of the budget rollup — the selected node(s)' consolidated lines presented as the
 * ordered rows of a PlanStatementTemplate, each already signed server-side (a cost row is negative), with
 * the bottom line as their sum.
 *
 * A statement SELECTS rather than partitions, so the rows deliberately do not have to add up to the
 * page's consolidated total: an account may feed two rows, or none. That is why the coverage advisories
 * the server returns are rendered here rather than hidden — a mis-authored template hides money silently,
 * which is the failure mode this whole view is designed against.
 */

import { Alert, Box, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { formatNumber } from '@/lib/locale';
import { RollupUnfilteredTotalRow, useFilteredTotalLabel } from './RollupFilteredTotal';
import { EMPTY_SHAPING, shapingQueryKey, shapingRequestFields, type RollupShaping } from './planRollupShaping';

interface StatementRow {
  rowCode: string;
  label: string;
  sortOrder: number;
  rowType: 'ACCOUNT_SET' | 'CALCULATION' | 'HEADER' | 'SPACER';
  isBold: boolean;
  /**
   * Already signed: income positive, cost negative. Null means the row has no figure at all — a header
   * or spacer, or a calculation the server could not evaluate — which is not the same as zero.
   */
  amount: number | null;
  accountCount: number;
  /** The row this one is additionally shown as a share of, as authored; drives the percent column. */
  percentOfRowCode?: string | null;
  /**
   * The row's value as a percentage of its base row (60 means 60 %). Null renders blank: no percent
   * presentation on this row, no figure of its own, or a zero denominator — never a made-up number.
   */
  percent?: number | null;
}

/** One statement the reader may switch to. */
interface TemplateOption {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  isDefault: boolean;
}

interface PlanRollupStatementDto {
  planStatementTemplateId?: string | null;
  templateName?: string | null;
  /**
   * The legal entity's active templates, in picker order. Supplied by the statement read rather than
   * fetched from the PlanStatementTemplate list endpoint: that one is gated on an authoring permission
   * a rollup reader need not hold, and only the server knows which legal entity the selection resolved
   * to. Every option here is therefore one this endpoint will render.
   */
  availableTemplates: TemplateOption[];
  rows: StatementRow[];
  /** The bottom line: the last calculation row, else Σ the account-set rows. */
  result: number;
  /** Set when `result` came from a row that is already printed — then no separate total is drawn. */
  resultRowCode?: string | null;
  /** Σ over accounts with budget that no row selects and that are not excluded by their sign. */
  uncoveredAmount: number;
  /** True only when a sign was guessed from an ambiguous account type. */
  resultIsApproximate: boolean;
  excludedAccountsAdvisory?: string | null;
  /** Whether a filter or period range narrowed this read — the server's answer, not the request's (§11). */
  isShaped: boolean;
  warnings: string[];
}

// POST like every other rollup read: the filter AST has no upper bound and does not belong in a request
// line, and every tab must narrow the same way or the tabs stop being comparable (§6, §9.1).
const ENDPOINT = '/api/finance/planbudgetrollup/statement';

const fmt0 = (v: number | null | undefined) => (v == null ? '' : formatNumber(v, { maximumFractionDigits: 0 }));
// NBSP before the % sign so the token never wraps mid-figure (the column width is a hint, not a
// cap — a wide share like "−211,8 %" must widen the column, not break onto two lines).
const fmtPct = (v: number | null | undefined) => (v == null ? '' : `${formatNumber(v, { maximumFractionDigits: 1 })} %`);

interface Props {
  versionId: string;
  orgUnitIds: string[];
  excludeOrgUnitIds?: string[];
  inclusion: 'SUBMITTED' | 'ALL';
  /** The statement to render. Undefined means "the legal entity's default", which the server picks. */
  templateId?: string;
  onTemplateChange?: (templateId: string) => void;
  /** The page-level filter and period window — every tab narrows on the same one (§9.1). */
  shaping?: RollupShaping;
  /** The page's unshaped consolidated total, printed beside a filtered one as the reference (§11). */
  unfilteredTotal?: number;
}

export function PlanRollupStatement({
  versionId,
  orgUnitIds,
  excludeOrgUnitIds = [],
  inclusion,
  templateId,
  onTemplateChange,
  shaping = EMPTY_SHAPING,
  unfilteredTotal,
}: Props) {
  const { t } = useTranslation();
  const filteredLabel = useFilteredTotalLabel();

  const { data, isFetching, isError, error } = useQuery<PlanRollupStatementDto, Error>({
    queryKey: [
      'planRollupStatement', versionId, [...orgUnitIds].sort(), [...excludeOrgUnitIds].sort(), inclusion,
      templateId ?? '', shapingQueryKey(shaping),
    ],
    queryFn: async () => {
      return (await apiClient.post<PlanRollupStatementDto>(ENDPOINT, {
        planVersionId: versionId,
        orgUnitIds,
        excludeOrgUnitIds,
        inclusion,
        planStatementTemplateId: templateId,
        ...shapingRequestFields(shaping),
      })).data;
    },
    enabled: !!versionId && orgUnitIds.length > 0,
  });

  // Only the first load blanks the tab. A refetch — which is what switching template is — keeps the
  // previous statement on screen, so the picker does not disappear from under the cursor that just
  // used it.
  if (isFetching && !data) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (isError) {
    return (
      <Alert severity="error">
        {error?.message ?? t('Finance.PlanRollup.StatementLoadFailed', { defaultValue: 'Failed to load the report.' })}
      </Alert>
    );
  }
  if (!data) return null;

  // A picker for a single statement is a control with one choice — it would only take up room and
  // imply there is something to decide. The heading already names the template being shown.
  const templates = data.availableTemplates ?? [];
  const showPicker = templates.length > 1;

  // The percent column exists when the template ASKS for one (a row authored with percentOfRowCode),
  // not when a percentage happens to be computable — a zero denominator must leave a blank cell in a
  // standing column, not make the whole column come and go with the figures.
  const hasPercentColumn = data.rows.some((r) => !!r.percentOfRowCode);
  const columnCount = hasPercentColumn ? 3 : 2;
  /** The label the percentage is "of" — the base row as the reader sees it, not its internal code. */
  const percentBaseLabel = (r: StatementRow) =>
    data.rows.find((base) => base.rowCode === r.percentOfRowCode)?.label || r.percentOfRowCode;

  return (
    <Box>
      {showPicker && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('Finance.PlanRollup.StatementTemplate', { defaultValue: 'Template' })}
          </Typography>
          <Select
            size="small"
            // The template the server actually rendered, not the one that was asked for: if a stale
            // link named a template this legal entity does not have, the read fell back to the default
            // and said so, and the picker must agree with the figures underneath it.
            value={data.planStatementTemplateId ?? ''}
            onChange={(e) => onTemplateChange?.(e.target.value as string)}
            sx={{ minWidth: 260 }}
            inputProps={{ 'aria-label': t('Finance.PlanRollup.StatementTemplate', { defaultValue: 'Template' }) }}
          >
            {templates.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                <Box>
                  <Typography variant="body2">
                    {tpl.name}
                    {tpl.isDefault && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {t('Finance.PlanRollup.StatementTemplateDefault', { defaultValue: '(default)' })}
                      </Typography>
                    )}
                  </Typography>
                  {tpl.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {tpl.description}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      {data.warnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
      {/* A deliberate, correct exclusion — reported so it is never silent, but at info severity: it is
          permanent on any tenant using internal transfer pricing, and a standing yellow alert for
          something that needs no action just teaches readers to ignore the list. */}
      {data.excludedAccountsAdvisory && (
        <Alert severity="info" sx={{ mb: 1 }}>{data.excludedAccountsAdvisory}</Alert>
      )}
      <Paper variant="outlined" sx={{ maxWidth: 720 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{data.templateName ?? t('Finance.PlanRollup.StatementColRow', { defaultValue: 'Row' })}</TableCell>
              <TableCell align="right">{t('Finance.PlanRollup.MeasureValue', { defaultValue: 'Value' })}</TableCell>
              {hasPercentColumn && (
                <TableCell align="right" sx={{ width: 80 }}>
                  {t('Finance.PlanRollup.StatementColPercent', { defaultValue: '%' })}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row) => {
              // A spacer is the blank line an author put there to group the rows above it; rendering it
              // as an empty row is the whole of its job.
              if (row.rowType === 'SPACER') {
                return (
                  <TableRow key={row.rowCode}>
                    <TableCell colSpan={columnCount} sx={{ height: 16, border: 0 }} />
                  </TableRow>
                );
              }

              // A header captions the rows below it and never carries a figure — bold regardless of the
              // row's own emphasis flag, since that is what makes it read as a caption rather than a row
              // whose value happens to be missing.
              const isHeader = row.rowType === 'HEADER';
              const bold = isHeader || row.isBold;
              // When the statement names its own bottom line there is no footer to carry the
              // approximate marker, so it belongs on that row instead — the figure it qualifies.
              const isBottomLine = row.rowCode === data.resultRowCode;

              return (
                <TableRow key={row.rowCode} hover={!isHeader}>
                  <TableCell sx={{ fontWeight: bold ? 700 : 400, pt: isHeader ? 2 : undefined }}>
                    {row.label}
                  </TableCell>
                  {/* Only the bottom line is coloured when negative, matching the By Account footer.
                      A cost row is negative by construction, so colouring it would paint the whole
                      statement red for behaving normally — and drown the one figure worth flagging.
                      Subtotals are already marked as subtotals by being bold; the two signals stay
                      separate. */}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: bold ? 700 : 400,
                      color: isBottomLine && (row.amount ?? 0) < 0 ? 'error.main' : 'inherit',
                    }}
                  >
                    {isBottomLine && data.resultIsApproximate ? (
                      <Tooltip
                        title={t('Finance.PlanRollup.SignedResultApproximate', {
                          defaultValue:
                            'At least one account has no reporting sign of its own and its account type cannot imply one, so this figure is approximate.',
                        })}
                      >
                        <span>≈ {fmt0(row.amount)}</span>
                      </Tooltip>
                    ) : (
                      fmt0(row.amount)
                    )}
                  </TableCell>
                  {hasPercentColumn && (
                    <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, color: 'text.secondary' }}>
                      {row.percent != null && (
                        // What the percentage is OF is the half of the figure the column heading cannot
                        // carry, since different rows may have different bases.
                        <Tooltip
                          title={t('Finance.PlanRollup.StatementPercentOf', {
                            defaultValue: 'As a share of {{label}}',
                            label: percentBaseLabel(row),
                          })}
                        >
                          <span>{fmtPct(row.percent)}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {data.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Typography variant="body2" color="text.secondary">
                    {t('Finance.PlanRollup.StatementEmpty', { defaultValue: 'This report has no rows yet.' })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            {/* A statement with calculation rows already ends with its own bottom line, so repeating it
                here would print the same figure twice under two different labels. Only a flat statement
                gets a synthesized total. */}
            {!data.resultRowCode && (
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>
                  {filteredLabel(
                    t('Finance.PlanRollup.StatementResult', { defaultValue: 'Result' }),
                    data.isShaped,
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: data.result < 0 ? 'error.main' : 'inherit' }}>
                  {data.resultIsApproximate ? (
                    <Tooltip
                      title={t('Finance.PlanRollup.SignedResultApproximate', {
                        defaultValue:
                          'At least one account has no reporting sign of its own and its account type cannot imply one, so this figure is approximate.',
                      })}
                    >
                      <span>≈ {fmt0(data.result)}</span>
                    </Tooltip>
                  ) : (
                    fmt0(data.result)
                  )}
                </TableCell>
                {hasPercentColumn && <TableCell />}
              </TableRow>
            )}
            {/* Money the template drops. The warning above names the accounts; this puts the figure
                beside the result it is missing from, where the size of the gap is readable. */}
            {data.uncoveredAmount !== 0 && (
              <TableRow>
                <TableCell sx={{ color: 'warning.main' }}>
                  {t('Finance.PlanRollup.StatementUncovered', { defaultValue: 'Not covered by any row' })}
                </TableCell>
                <TableCell align="right" sx={{ color: 'warning.main' }}>{fmt0(data.uncoveredAmount)}</TableCell>
                {hasPercentColumn && <TableCell />}
              </TableRow>
            )}
            <RollupUnfilteredTotalRow isShaped={data.isShaped} unfilteredTotal={unfilteredTotal} colSpan={columnCount} />
          </TableFooter>
        </Table>
      </Paper>
    </Box>
  );
}

export default PlanRollupStatement;
